/**
 * Theme-level accessibility fixes for Shopify Forms, PDP content, and Judge.me widgets.
 */
const initTaxExemptionFormA11y = () => {
  const setupEmbed = (embed) => {
    if (!embed || embed.dataset.a11yFormSetup === 'true') return;
    embed.dataset.a11yFormSetup = 'true';

    const applyFixes = (shadow) => {
      if (!shadow) return false;

      const hasFields = shadow.querySelector('input:not([type="hidden"]), select, textarea');
      if (!hasFields) return false;

      fixShopifyFormFieldLabels(shadow);
      fixShopifyFormFileUpload(shadow);
      return true;
    };

    let shadowObserver;

    const tryApply = () => {
      if (!embed.shadowRoot) return;

      applyFixes(embed.shadowRoot);

      if (!shadowObserver) {
        shadowObserver = new MutationObserver(tryApply);
        shadowObserver.observe(embed.shadowRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-labelledby', 'id', 'for'],
        });
      }
    };

    let attempts = 0;
    const retry = window.setInterval(() => {
      attempts += 1;
      tryApply();
      if (attempts >= 20) {
        window.clearInterval(retry);
      }
    }, 500);

    const embedObserver = new MutationObserver(tryApply);
    embedObserver.observe(embed, { childList: true, subtree: true });

    tryApply();
  };

  const existingEmbed = document.querySelector('shopify-forms-embed');
  if (existingEmbed) {
    setupEmbed(existingEmbed);
  }

  const pageObserver = new MutationObserver(() => {
    const embed = document.querySelector('shopify-forms-embed');
    if (embed) setupEmbed(embed);
  });
  pageObserver.observe(document.body, { childList: true, subtree: true });
};

const fixShopifyFormFieldLabels = (shadow) => {
  shadow.querySelectorAll('input:not([type="hidden"]):not([type="file"]), select, textarea').forEach((input) => {
    const label =
      input.labels?.[0] ||
      (input.id ? shadow.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);

    if (label && input.id && label.htmlFor !== input.id) {
      label.htmlFor = input.id;
    }

    if (input.hasAttribute('aria-labelledby')) {
      input.removeAttribute('aria-labelledby');
    }

    // Native <label for> association is sufficient when it works; avoid duplicate aria-label.
    const hasNativeLabel = Boolean(label && input.labels?.length);
    if (hasNativeLabel && input.hasAttribute('aria-label') && input.dataset.a11yAriaLabel !== 'true') {
      input.removeAttribute('aria-label');
    } else if (!hasNativeLabel) {
      const labelText = label?.textContent?.replace(/\s+/g, ' ').trim();
      if (labelText) {
        input.setAttribute('aria-label', labelText);
        input.dataset.a11yAriaLabel = 'true';
      }
    }
  });
};

const injectShopifyFormFileStyles = (shadow) => {
  if (shadow.querySelector('[data-a11y-form-file-styles]')) return;

  const style = document.createElement('style');
  style.setAttribute('data-a11y-form-file-styles', 'true');
  style.textContent = `
    button[data-a11y-remove-file] {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-top: 0.8rem;
      margin-bottom: 8px;
      padding: 10px 16px;
      border: 1px solid #c62828;
      border-radius: 4px;
      background: transparent;
      color: #c62828;
      font-size: 12px;
      line-height: 1.2;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    button[data-a11y-remove-file]:hover,
    button[data-a11y-remove-file]:focus-visible {
      background: #c62828;
      color: #fff;
    }

    button[data-a11y-remove-file] svg {
      display: none;
    }
  `;
  shadow.appendChild(style);
};

const fixShopifyFormFileUpload = (shadow) => {
  const fileInput = shadow.querySelector('input[type="file"]');
  if (!fileInput || fileInput.dataset.a11yFileFixed === 'true') return;

  injectShopifyFormFileStyles(shadow);

  const fileField =
    fileInput.closest('[class*="_formFileInputField"]') ||
    fileInput.closest('[class*="formFileInputField"]') ||
    fileInput.parentElement;

  if (!fileField) return;

  const fileInputContainer = fileField.parentElement;
  if (!fileInputContainer) return;

  fileInput.dataset.a11yFileFixed = 'true';

  const getClearButton = () =>
    fileInputContainer.querySelector(
      'button[id$="-clear"], button[data-a11y-remove-file], button[aria-label*="Clear" i], button[aria-label*="Remove" i], [class*="CancelButton"]'
    ) ||
    shadow.querySelector(`button[id="${CSS.escape(fileInput.id.replace(/-field$/, '-clear'))}"]`);

  const getDisplayedFileName = () => {
    const nameEl = fileField.querySelector('[class*="FileName"]');
    return nameEl?.textContent?.replace(/\s+/g, ' ').trim() || fileInput.files?.[0]?.name || '';
  };

  let applyingFileFixes = false;

  const applyFileUploadFixes = () => {
    if (applyingFileFixes) return;
    applyingFileFixes = true;

    try {
      const fileLabel = fileInput.closest('label');
      if (fileLabel?.contains(fileInput)) {
        fileLabel.parentElement?.insertBefore(fileInput, fileLabel);

        if (!fileLabel.hasAttribute('for') && fileInput.id) {
          fileLabel.setAttribute('for', fileInput.id);
        }

        fileLabel.setAttribute('aria-hidden', 'true');
        fileLabel.setAttribute('tabindex', '-1');
      }

      fileField.querySelectorAll('[class*="FileInputButton"], [class*="FileName"]').forEach((element) => {
        if (element.getAttribute('aria-hidden') !== 'true') {
          element.setAttribute('aria-hidden', 'true');
        }
      });

      const fileName = getDisplayedFileName();
      const nextAriaLabel = fileName
        ? `${fileName} uploaded - select to add a different file`
        : 'Add file';

      if (fileInput.getAttribute('aria-label') !== nextAriaLabel) {
        fileInput.setAttribute('aria-label', nextAriaLabel);
      }

      const clearButton = getClearButton();
      if (!clearButton) return;

      if (clearButton.parentElement !== fileInputContainer) {
        fileInputContainer.appendChild(clearButton);
      }

      if (!clearButton.hasAttribute('data-a11y-remove-file')) {
        clearButton.setAttribute('data-a11y-remove-file', 'true');
      }

      if (clearButton.getAttribute('aria-label') !== 'Remove uploaded file') {
        clearButton.setAttribute('aria-label', 'Remove uploaded file');
      }

      const icon = clearButton.querySelector('svg');
      if (icon && icon.getAttribute('aria-hidden') !== 'true') {
        icon.setAttribute('aria-hidden', 'true');
      }

      let removeText = clearButton.querySelector('[data-a11y-remove-text]');
      if (!removeText) {
        removeText = document.createElement('span');
        removeText.setAttribute('data-a11y-remove-text', 'true');
        clearButton.appendChild(removeText);
      }

      if (removeText.textContent !== 'Remove') {
        removeText.textContent = 'Remove';
      }
    } finally {
      applyingFileFixes = false;
    }
  };

  let debounceId;
  const scheduleFileUploadFixes = () => {
    window.clearTimeout(debounceId);
    debounceId = window.setTimeout(applyFileUploadFixes, 100);
  };

  fileInput.addEventListener('change', scheduleFileUploadFixes);

  if (!fileInputContainer.dataset.a11yRemoveListener) {
    fileInputContainer.dataset.a11yRemoveListener = 'true';
    fileInputContainer.addEventListener('click', (event) => {
      if (event.target.closest('button[id$="-clear"], button[data-a11y-remove-file], [class*="CancelButton"]')) {
        scheduleFileUploadFixes();
      }
    });
  }

  const fileObserver = new MutationObserver((mutations) => {
    const isRelevant = mutations.some((mutation) => {
      if (mutation.type !== 'childList') return false;
      return Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('button[id$="-clear"], [class*="CancelButton"], [class*="FileName"]') ||
            node.querySelector?.('button[id$="-clear"], [class*="CancelButton"], [class*="FileName"]'))
      );
    });

    if (isRelevant) scheduleFileUploadFixes();
  });

  fileObserver.observe(fileInputContainer, { childList: true, subtree: true });

  applyFileUploadFixes();
};

const initSpecificationTableHeaders = () => {
  document.querySelectorAll('.accordion__content table').forEach((table) => {
    if (table.dataset.a11yRowHeaders === 'true') return;
    table.dataset.a11yRowHeaders = 'true';

    table.querySelectorAll('tr').forEach((row) => {
      if (row.querySelector('th')) return;

      const cells = row.querySelectorAll('td');
      if (cells.length !== 2) return;

      const headerCell = cells[0];
      const th = document.createElement('th');
      th.scope = 'row';
      th.innerHTML = headerCell.innerHTML;
      headerCell.replaceWith(th);
    });
  });
};

let judgeMeObserver;

const JUDGE_ME_SECONDARY_TEXT_COLOR = '#373737';

const fixJudgeMeContrastColors = () => {
  document
    .querySelectorAll(
      '#judgeme_product_reviews .jdgm-rev-widg__summary-text, #judgeme_product_reviews .jdgm-rev-widg__summary-average, #judgeme_product_reviews .jdgm-histogram__frequency, #judgeme_product_reviews .jdgm-rev__timestamp, #judgeme_product_reviews .jdgm-rev__location, .jdgm-rev-widg .jdgm-rev-widg__summary-text, .jdgm-rev-widg .jdgm-rev-widg__summary-average, .jdgm-rev-widg .jdgm-histogram__frequency, .jdgm-rev-widg .jdgm-rev__timestamp, .jdgm-rev-widg .jdgm-rev__location'
    )
    .forEach((element) => {
      if (element.style.getPropertyValue('color') === JUDGE_ME_SECONDARY_TEXT_COLOR) return;
      element.style.setProperty('color', JUDGE_ME_SECONDARY_TEXT_COLOR, 'important');
    });
};

const initJudgeMeA11y = () => {
  fixJudgeMeStarLabels();
  fixJudgeMePagination();
  fixJudgeMeDecorativeIcons();
  fixJudgeMeContrastColors();

  if (judgeMeObserver) return;

  judgeMeObserver = new MutationObserver((mutations) => {
    const hasJudgeMeMutation = mutations.some((mutation) => {
      const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
      if (target?.closest?.('.jdgm-widget, #judgeme_product_reviews')) return true;

      return Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('.jdgm-widget, #judgeme_product_reviews') ||
            node.querySelector?.('.jdgm-widget, #judgeme_product_reviews'))
      );
    });

    if (!hasJudgeMeMutation) return;

    fixJudgeMeStarLabels();
    fixJudgeMePagination();
    fixJudgeMeDecorativeIcons();
    fixJudgeMeContrastColors();
  });
  judgeMeObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-label', 'data-average-rating'],
  });
};

const fixJudgeMeStarLabels = () => {
  document.querySelectorAll('.jdgm-prev-badge, .jdgm-rev-widg__summary').forEach((badge) => {
    const stars =
      badge.querySelector('[role="img"]') ||
      badge.querySelector('.jdgm-rev-widg__summary-stars') ||
      badge.querySelector('.jdgm-star-all');
    if (!stars) return;

    const ratingSource = badge.closest('[data-average-rating]') || badge.querySelector('[data-average-rating]');
    const existingLabel = stars.getAttribute('aria-label') || '';
    const rating =
      ratingSource?.dataset?.averageRating ||
      ratingSource?.getAttribute('data-average-rating') ||
      existingLabel.match(/\d+(?:\.\d+)?/)?.[0];

    if (rating) {
      const label = `Average rating is ${rating} out of 5 stars`;
      if (stars.getAttribute('aria-label') === label) return;

      stars.setAttribute('aria-label', `Average rating is ${rating} out of 5 stars`);
    }
  });
};

const fixJudgeMePagination = () => {
  document.querySelectorAll('.jdgm-paginate').forEach((paginate) => {
    if (paginate.querySelector('nav.jdgm-paginate-nav')) return;

    const pageLinks = Array.from(paginate.querySelectorAll('a, button, span.jdgm-paginate__page'));
    if (!pageLinks.length) return;

    const nav = document.createElement('nav');
    nav.className = 'jdgm-paginate-nav';
    nav.setAttribute('aria-label', 'reviews pagination');
    const list = document.createElement('ul');

    pageLinks.forEach((link) => {
      const item = document.createElement('li');
      if (
        link.classList.contains('jdgm-curt') ||
        link.classList.contains('jdgm-paginate__page--current') ||
        link.getAttribute('aria-current') === 'page'
      ) {
        link.setAttribute('aria-current', 'page');
      }
      item.appendChild(link);
      list.appendChild(item);
    });

    nav.appendChild(list);
    paginate.innerHTML = '';
    paginate.appendChild(nav);
  });
};

const fixJudgeMeDecorativeIcons = () => {
  document.querySelectorAll('.jdgm-rev__author-wrapper .jdgm-rev__author-badge, .jdgm-rev__buyer-badge').forEach((icon) => {
    icon.setAttribute('aria-hidden', 'true');
  });
};

const initProductScrollPadding = () => {
  const main = document.getElementById('MainContent');
  if (!main || main.dataset.template !== 'product') return;

  const updateScrollPadding = () => {
    const quickOrderTotal = document.querySelector('.quick-order-list__total');
    const header = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
    const totalHeight = quickOrderTotal ? quickOrderTotal.offsetHeight : 0;
    const headerHeight = header ? header.offsetHeight : 0;
    const padding = totalHeight + headerHeight + 16;

    document.documentElement.style.setProperty('--scroll-padding-bottom', `${padding}px`);
    document.documentElement.style.scrollPaddingBottom = `${padding}px`;
  };

  updateScrollPadding();
  window.addEventListener('resize', updateScrollPadding);

  const quickOrderList = document.querySelector('quick-order-list');
  if (quickOrderList) {
    const observer = new MutationObserver(updateScrollPadding);
    observer.observe(quickOrderList, { childList: true, subtree: true, attributes: true });
  }
};

const initAccessibility = () => {
  initTaxExemptionFormA11y();
  initSpecificationTableHeaders();
  initJudgeMeA11y();
  initProductScrollPadding();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccessibility);
} else {
  initAccessibility();
}

document.addEventListener('shopify:section:load', initAccessibility);
